import { Intercom } from "@intercom/messenger-js-sdk";
import { getActiveIntercomAppId } from "@/constants/intercom";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { Endpoints } from "@utils/api/endpoints.ts";
import axios from "axios";
import { AXIOS_CONFIG } from "@utils/api.ts";
import { backend_url } from "@/constants.ts";
import { getIntercomAttributes } from "@utils/debug/index";
import { useIntercomStore } from "./stores/intercomStore";
import { intercomHelper } from "./intercom/IntercomHelper";
import { createDebugSession } from "./debug/debugSessionService";
import { emitDebugEvent } from "@utils/debug/eventIntegration";

/**
 * Fetch Intercom JWT token from the backend.
 *
 * @returns Promise<string | null> - JWT token or null if fetch failed
 */
const fetchIntercomJWT = async (): Promise<string | null> => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.onboarding.intercomJwt}`,
      AXIOS_CONFIG
    );

    // Backend converts snake_case to camelCase via return_response, so we need intercomJwt
    const jwt = response.data.content?.intercomJwt || null;
    return jwt;
  } catch (error) {
    console.error("[Intercom] Failed to fetch JWT");
    return null;
  }
};

/**
 * Fetch user context data from the backend for Intercom attributes.
 *
 * @returns Promise<Record<string, any> | null> - User context data or null if fetch failed
 */
const fetchUserContext = async (): Promise<Record<string, any> | null> => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.onboarding.intercomContext}`,
      AXIOS_CONFIG
    );

    return response.data.content || null;
  } catch (error) {
    console.error("[Intercom] Failed to fetch user context");
    return null;
  }
};

/**
 * Initialize Intercom with user data and context attributes.
 *
 * Includes:
 * - Basic user info (name, email, role)
 * - Browser/device context (from client-side detection)
 * - User context from backend (tax software, intake system, extension status)
 *
 * @returns Promise<boolean> - True if initialization was successful, false otherwise
 */
export const initializeIntercom = async (): Promise<boolean> => {
  try {
    const { user, isTeamMember, role } = useAuthStore.getState();

    const activeAppId = getActiveIntercomAppId();

    if (user) {
      // Fetch Intercom JWT token for secure authentication
      const intercomJwt = await fetchIntercomJWT();

      // Fetch user context from backend (tax software, intake system, extension status, role)
      const userContext = await fetchUserContext();

      // Filter out undefined/null values - Intercom shows "Unknown" for null values
      // Keep empty strings, 0, and false as they are valid values
      const filterUndefined = (
        obj: Record<string, any>
      ): Record<string, any> => {
        const filtered: Record<string, any> = {};
        for (const [key, value] of Object.entries(obj)) {
          // Filter out undefined and null (Intercom shows "Unknown" for these)
          // Keep empty strings, 0, false, and other falsy-but-valid values
          if (value !== undefined && value !== null) {
            filtered[key] = value;
          }
        }
        return filtered;
      };

      // Hide Intercom widget for taxpayers (clients)
      const isClient = role === "client" || user.role === "client";

      // Build Intercom configuration with user-level attributes only
      // Device-specific attributes (browser, OS, deviceType) are NOT included here
      // They are captured per-conversation when messages are sent (see captureMessageContext)
      const intercomConfig: any = {
        app_id: activeAppId,
        user_id: user?.id,
        name: user.firstName + " " + user.lastName,
        email: user.email,
        hide_default_launcher: isClient,
        // User role (display role for Intercom)
        customer_role: isTeamMember() ? "Team Member" : "Owner",
        // User context from backend (tax software, intake system, extension status) - filtered
        ...filterUndefined(userContext || {}),
      };

      // Add JWT token if available (using intercom_user_jwt for JWT-based authentication)
      if (intercomJwt) {
        intercomConfig.intercom_user_jwt = intercomJwt;
      }

      Intercom(intercomConfig);

      // Set up automatic device context tracking when messenger is shown
      setupMessageContextTracking();

      // Explicitly update attributes after initialization
      // This is required when "Require verified updates" is enabled in Intercom
      // The JWT token must be explicitly set before the update call
      const attributesToUpdate = filterUndefined({
        tax_prep_software: userContext?.tax_prep_software,
        intake_system: userContext?.intake_system,
        extension_installed: userContext?.extension_installed,
        extension_version: userContext?.extension_version,
        user_role: userContext?.user_role,
      });

      if (Object.keys(attributesToUpdate).length > 0) {
        // Use a small delay to ensure Intercom is fully initialized
        setTimeout(() => {
          if (typeof window !== "undefined" && window.Intercom) {
            // Explicitly set JWT before update to ensure authentication works
            // This is required when "Require verified updates" is enabled
            if (intercomJwt) {
              window.Intercom("setUserJwt", intercomJwt);
            }

            // Now update attributes with JWT authentication
            window.Intercom("update", attributesToUpdate);
          }
        }, 500);
      }
    } else {
      // For non-authenticated users, show Intercom widget
      Intercom({
        app_id: activeAppId,
        hide_default_launcher: false,
      });
    }

    // ``initializeIntercom`` uses the SDK directly; ``showIntercom`` / surveys / events
    // gate on ``useIntercomStore.isInitialized`` (see ``intercomStore``).
    useIntercomStore.setState({ isInitialized: true, isInitializing: false });

    return true;
  } catch (error) {
    console.error("Error initializing Intercom:", error);
    return false;
  }
};

/**
 * Start a survey by survey ID
 * This is a wrapper around the store method for backward compatibility.
 *
 * @param surveyId - The survey ID to start
 * @returns boolean - True if survey was started successfully, false otherwise
 */
export const startSurvey = (surveyId: number | string): boolean => {
  return useIntercomStore.getState().startSurvey(surveyId);
};

/**
 * Track custom events in Intercom
 * This is a wrapper around the store method for backward compatibility.
 *
 * @param eventName - The name of the event to track
 * @param metadata - Optional metadata to include with the event
 * @returns boolean - True if event was tracked successfully, false otherwise
 */
export const trackEvent = (
  eventName: string,
  metadata?: Record<string, any>
): boolean => {
  return useIntercomStore.getState().trackEvent(eventName, metadata);
};

/**
 * Show the Intercom messenger widget.
 * Only shows if Intercom has been initialized.
 * @returns boolean - True if shown successfully, false if not initialized
 */
export const showIntercom = (): boolean => {
  return useIntercomStore.getState().showIntercom();
};

/**
 * Shutdown Intercom and clear the session.
 * Call this on logout to ensure clean state for the next user.
 * @returns boolean - True if shutdown was successful, false otherwise
 */
export const shutdownIntercom = (): boolean => {
  try {
    if (typeof window !== "undefined" && window.Intercom) {
      window.Intercom("shutdown");
    }

    // Reset the Intercom store state
    useIntercomStore.getState().reset();

    // Clear JWT cache in IntercomHelper
    intercomHelper.clearJWTCache();

    return true;
  } catch (error) {
    console.error("[Intercom] Error shutting down");
    return false;
  }
};

/**
 * Update Intercom user attributes.
 *
 * Used to update user attributes in Intercom, such as when a debug session is created.
 * This allows support to see the latest user context in Intercom conversations.
 *
 * @param attributes - Object with attribute names and values to update
 * @returns Promise<boolean> - True if update was successful, false otherwise
 */
export const updateIntercomAttributes = async (
  attributes: Record<string, any>
): Promise<boolean> => {
  return await intercomHelper.updateAttributesDirect(attributes);
};

/**
 * Refresh Intercom user context from backend.
 *
 * This is the recommended way to update Intercom when user context changes.
 * It fetches fresh data from the backend and updates Intercom attributes.
 *
 * Usage:
 * - After extension heartbeat: refreshIntercomUserContext('extension')
 * - After settings change: refreshIntercomUserContext('settings')
 * - Refresh all: refreshIntercomUserContext() or refreshIntercomUserContext('all')
 *
 * @param contextType - Type of context to refresh: 'extension', 'settings', 'role', or 'all'
 * @returns Promise<boolean> - True if update was successful
 */
export const refreshIntercomUserContext = async (
  contextType: "extension" | "settings" | "role" | "all" = "all"
): Promise<boolean> => {
  switch (contextType) {
    case "extension":
      return await intercomHelper.refreshExtensionStatus();
    case "settings":
      return await intercomHelper.refreshSettings();
    case "role":
      return await intercomHelper.refreshUserRole();
    case "all":
    default:
      return await intercomHelper.refreshAll();
  }
};

/**
 * Add debug session ID to the current conversation.
 *
 * This function should be called when a debug session is created (when messenger opens).
 * Sends the debug_session_id as a conversation event, not a user attribute.
 * This ensures each conversation has its own debug session ID, so multiple conversations
 * don't overwrite each other.
 *
 * CRITICAL: Support summary is made visible directly in the conversation.
 * - Summary appears as a pre-populated message (visible immediately)
 * - Summary is also included in event metadata for timeline
 * - Support can read the summary without clicking or inspecting
 *
 * @param debugSessionId - The UUID of the debug session
 * @param supportSummary - Human-readable summary (3-5 sentences) for support
 * @returns boolean - True if event was tracked successfully, false otherwise
 */
export const updateIntercomWithDebugSession = (
  debugSessionId: string,
  supportSummary?: string
): boolean => {
  try {
    if (typeof window === "undefined" || !window.Intercom) {
      return false;
    }

    const debugSessionUrl = `${window.location.origin}/debug/${debugSessionId}`;

    // CRITICAL: Make summary visible directly in conversation
    // Pre-populate the message input with the summary so support sees it immediately
    if (supportSummary) {
      try {
        // Format summary for visibility in Intercom
        const formattedSummary = `🔍 Debug Session Summary\n\n${supportSummary}\n\n📋 View full debug session: ${debugSessionUrl}`;

        // Use showNewMessage to display summary prominently
        // This pre-populates the message input, making it visible to support
        window.Intercom("showNewMessage", formattedSummary);
      } catch (summaryError) {
        // Fallback: if showNewMessage fails, continue with event tracking
      }
    }

    // Also send as an event tied to the current conversation (for timeline)
    const eventData: Record<string, any> = {
      debug_session_id: debugSessionId,
      debug_session_url: debugSessionUrl,
      created_at: new Date().toISOString(),
    };

    // Include support summary in event metadata (for timeline/search)
    if (supportSummary) {
      eventData.debug_summary = supportSummary;
    }

    return trackEvent("debug_session_created", eventData);
  } catch (error) {
    console.error("[Intercom] Error adding debug session");
    return false;
  }
};

/**
 * Capture device context for the current conversation.
 *
 * This function captures browser/device context and tracks it as an Intercom event
 * associated with the current conversation. This ensures support can see the device
 * context for each specific conversation, even if the user switches devices later.
 *
 * Device context is captured:
 * - When the messenger is shown/opened
 * - Before sending a message (if called explicitly)
 * - As part of debug session creation
 *
 * @returns boolean - True if context was captured successfully, false otherwise
 */
let lastContextCaptureTime = 0;
const CONTEXT_CAPTURE_THROTTLE_MS = 5000; // Don't capture more than once per 5 seconds

export const captureMessageContext = (): boolean => {
  try {
    if (typeof window === "undefined" || !window.Intercom) {
      return false;
    }

    // Throttle: Don't capture if we just captured recently (within 5 seconds)
    const now = Date.now();
    if (now - lastContextCaptureTime < CONTEXT_CAPTURE_THROTTLE_MS) {
      return false;
    }

    // Get current device context
    const browserContext = getIntercomAttributes();

    // Filter out undefined/null values
    const filterUndefined = (obj: Record<string, any>): Record<string, any> => {
      const filtered: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== null) {
          filtered[key] = value;
        }
      }
      return filtered;
    };

    const deviceContext = filterUndefined(browserContext);

    // Track as an event - this associates the device context with the current conversation
    // The event will appear in the conversation timeline for support to see
    const eventTracked = trackEvent("message_context", {
      // Device/browser information
      browser: deviceContext.browser,
      browserVersion: deviceContext.browserVersion,
      operatingSystem: deviceContext.operatingSystem,
      deviceType: deviceContext.deviceType,
      // Technical identifiers for filtering
      browserTechnical: deviceContext.browserTechnical,
      osTechnical: deviceContext.osTechnical,
      deviceTechnical: deviceContext.deviceTechnical,
      // Timestamp for reference
      captured_at: new Date().toISOString(),
    });

    if (eventTracked) {
      lastContextCaptureTime = now; // Update last capture time
    }

    return eventTracked;
  } catch (error) {
    console.error("[Intercom] Error capturing message context");
    return false;
  }
};

/**
 * Track if we've created a session for the current Intercom open
 * Reset when messenger closes (new conversation)
 */
let sessionCreatedForOpen = false;

/**
 * Set up automatic device context tracking and debug session creation when Intercom messenger is shown.
 *
 * V1 Approach: Trigger on Intercom Open
 * - Use Intercom's `onShow` event (more reliable than message detection)
 * - Add guard: `sessionCreatedForOpen` to prevent duplicates
 * - Trigger debug session creation when messenger opens
 *
 * This ensures device context is captured automatically when users open the messenger,
 * so support always has the device context for each conversation.
 *
 * The throttle in captureMessageContext() prevents duplicate captures.
 */
const setupMessageContextTracking = (): void => {
  try {
    if (typeof window === "undefined" || !window.Intercom) {
      return;
    }

    // Set up Intercom onShow listener for debug session creation
    window.Intercom("onShow", async () => {
      // Emit debug event for support opened
      const { user } = useAuthStore.getState();
      emitDebugEvent("support", "SUPPORT_OPENED", "web", {
        userId: user?.id || "unknown",
        url: window.location.href,
      });

      // Handle reopen: If messenger was closed and reopened, allow new session
      // The guard resets on onHide, so reopen creates a fresh session
      if (!sessionCreatedForOpen) {
        sessionCreatedForOpen = true;

        try {
          await createDebugSession();
        } catch (error) {
          // Reset guard on failure so retry can happen on next open
          sessionCreatedForOpen = false;
          // Don't block user - continue even if debug session fails
        }
      }
    });

    // Reset guard when messenger closes (allows new session on reopen)
    window.Intercom("onHide", () => {
      sessionCreatedForOpen = false;
    });

    // Only capture context once when messenger is first shown
    // The throttle in captureMessageContext() will prevent duplicates
    // Use a delay to ensure Intercom is fully initialized
    setTimeout(() => {
      captureMessageContext();
    }, 1000);

    // Note: Removed click listener to prevent duplicate captures
    // The initial capture is sufficient, and if needed, explicit calls
    // to captureMessageContext() can be made when messages are sent
  } catch (error) {
    console.error("[Intercom] Error setting up message context tracking");
  }
};
